import React, { useState, useEffect } from 'react';
import '../shared.css';
import Sidebar from '../Components/Layout/sidebar';
import Topbar from '../Components/Layout/topbar';
import Analytic_cards from '../Components/Comman/Analytics_card';
import Button from '../Components/Comman/Button';
import { supabase } from '../supabaseClient';

/* ── Mini stat row ── */
const StatRow = ({ label, value, pct }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 0', borderBottom: '1px solid rgba(42,42,42,0.08)',
  }}>
    <span style={{ fontFamily: 'Inter', fontSize: 13, color: '#191919' }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 600, color: '#191919' }}>{value}</span>
      {pct !== undefined && (
        <span style={{
          fontFamily: 'Inter', fontSize: 11, fontWeight: 600,
          color: pct >= 0 ? '#2d7a2d' : '#a33',
          background: pct >= 0 ? 'rgba(74,148,74,0.12)' : 'rgba(180,60,60,0.1)',
          padding: '2px 8px', borderRadius: 99,
        }}>{pct >= 0 ? `+${pct}%` : `${pct}%`}</span>
      )}
    </div>
  </div>
);

/* ── Recent activity item ── */
const ActivityItem = ({ name, action, time }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 0', borderBottom: '1px solid rgba(42,42,42,0.07)',
    transition: 'background 0.15s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,240,232,0.5)'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', background: '#D6B893',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Inter', fontSize: 12, fontWeight: 700, color: '#0D0D0D', flexShrink: 0,
      }}>{(name || '?')[0].toUpperCase()}</div>
      <div>
        <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 500, color: '#191919' }}>{name}</p>
        <p style={{ fontFamily: 'Inter', fontSize: 11, color: '#8B8B7A' }}>{action}</p>
      </div>
    </div>
    <span style={{ fontFamily: 'Inter', fontSize: 11, color: '#8B8B7A', whiteSpace: 'nowrap', marginLeft: 10 }}>{time}</span>
  </div>
);

const Dashboard = () => {
  const [stats, setStats]       = useState(null);
  const [tickets, setTickets]   = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      /* ── bookings aggregate — FIX: table is 'booking' not 'bookings' ── */
      const { data: bookings, error: bErr } = await supabase
        .from('bookings')
        .select('total_price, payment_status, user_name, ticket_title, quantity, booked_at');
      if (bErr) throw new Error('Bookings: ' + bErr.message);

      const totalRevenue  = bookings.reduce((s, b) => s + (Number(b.total_price) || 0), 0);
      const totalVisitors = bookings.reduce((s, b) => s + (Number(b.quantity) || 0), 0);

      /* ── tickets aggregate ── */
      const { data: ticketRows, error: tErr } = await supabase
        .from('tickets')
        .select('title, sold, capacity, status');
      if (tErr) throw new Error('Tickets: ' + tErr.message);

      const activeTicketsSold = ticketRows
        .filter(t => t.status === 'Active')
        .reduce((s, t) => s + (Number(t.sold) || 0), 0);

      /* ── exhibitors count ── */
      const { count: exhibitorCount, error: eErr } = await supabase
        .from('exhibitors')
        .select('id', { count: 'exact', head: true });
      if (eErr) throw new Error('Exhibitors: ' + eErr.message);

      setStats({
        revenue:       `EGP ${totalRevenue.toLocaleString()}`,
        visitors:      totalVisitors.toLocaleString(),
        activeTickets: activeTicketsSold.toLocaleString(),
        exhibitors:    exhibitorCount ?? 0,
      });

      setTickets(ticketRows);

      /* ── recent bookings for activity feed ── */
      const recent = [...bookings]
        .sort((a, b) => new Date(b.booked_at) - new Date(a.booked_at))
        .slice(0, 6)
        .map(b => ({
          name:   b.user_name,
          action: `Booked ${b.ticket_title} × ${b.quantity}`,
          time:   timeAgo(b.booked_at),
        }));
      setActivity(recent);
    } catch (err) {
      console.error('Dashboard fetch error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <Sidebar />
      <div className='pagestrcutre'>
        <Topbar />

        <div className='pagetop_div'>
          <div className='pagetop_div1'>
            <h3 className='pagetop_text1'>Dashboard</h3>
            <Button text='Refresh' icon={null} onClick={fetchDashboard} />
          </div>
          <p className='pagetop_text2'>Welcome back — here's what's happening today.</p>
        </div>

        {loading ? (
          <p className='pagetop_text2' style={{ padding: '0 50px' }}>Loading dashboard…</p>
        ) : error ? (
          <div style={{ padding: '0 50px' }}>
            <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#a33', background: 'rgba(180,60,60,0.08)', padding: '12px 16px', borderRadius: 8 }}>
              ⚠ {error}
            </p>
          </div>
        ) : (
          <>
            <div className='analytics_div'>
              <Analytic_cards text1='Total Revenue'  text2={stats?.revenue       ?? '—'} text3='Live from Supabase' />
              <Analytic_cards text1='Total Visitors' text2={stats?.visitors      ?? '—'} text3='Cumulative ticket qty' />
              <Analytic_cards text1='Active Tickets' text2={stats?.activeTickets ?? '—'} text3='Sold across active types' />
              <Analytic_cards text1='Exhibitors'     text2={stats?.exhibitors    ?? '—'} text3='Total registered' />
            </div>

            <div className='page_content' style={{ display: 'flex', gap: 20, paddingBottom: 30 }}>
              {/* Ticket performance */}
              <div style={{ flex: 1, background: 'rgba(214,184,147,0.63)', borderRadius: 8, padding: 24 }}>
                <h4 style={{ fontFamily: '"Cormorant Garamond"', fontSize: 20, fontWeight: 600, color: '#191919', marginBottom: 16 }}>
                  Ticket Performance
                </h4>
                {tickets.length === 0
                  ? <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#8B8B7A' }}>No ticket data yet.</p>
                  : tickets.map(t => {
                      const pct = t.capacity > 0 ? Math.round((t.sold / t.capacity) * 100) : 0;
                      return (
                        <StatRow
                          key={t.title}
                          label={t.title}
                          value={`${Number(t.sold).toLocaleString()} sold`}
                          pct={pct}
                        />
                      );
                    })
                }
              </div>

              {/* Recent activity */}
              <div style={{ flex: 1, background: 'rgba(214,184,147,0.63)', borderRadius: 8, padding: 24 }}>
                <h4 style={{ fontFamily: '"Cormorant Garamond"', fontSize: 20, fontWeight: 600, color: '#191919', marginBottom: 16 }}>
                  Recent Activity
                </h4>
                {activity.length === 0
                  ? <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#8B8B7A' }}>No recent bookings.</p>
                  : activity.map((a, i) => <ActivityItem key={i} {...a} />)
                }
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

/* ── helper ── */
function timeAgo(isoString) {
  if (!isoString) return '—';
  const diff = (Date.now() - new Date(isoString)) / 1000;
  if (diff < 60)    return `${Math.floor(diff)}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}

export default Dashboard;